import { http } from '@/lib/http'
import type { Lease, LeaseCreateInput, LeaseCreateResponse, LeaseListResponse, LeaseStatus, RentListResponse } from '@/types/models'

export interface LeaseListParams {
  page?: number
  size?: number
  propertyId?: string
  tenantId?: string
  status?: LeaseStatus
  /** Ended leases (expired + canceled), newest end first; wins over `endingInDays`. */
  history?: boolean
  /** Leaves out leases on archived properties. */
  dropdown?: boolean
  /** 0–365; alone it implies `status=active`. */
  endingInDays?: number
  /** Tenant first/last name or property title. */
  search?: string
  facets?: boolean
  /** ISO2: that country's leases only (LP-491). Absent = every country. */
  country?: string
}

export const LEASE_ENDING_SOON_DAYS = 30

/** Lease endpoints (api_doc/lease/*.md; the list is `/v2/leases`, the rest `/leases`). */
export const leaseApi = {
  list: (params: LeaseListParams) => http.get<LeaseListResponse>('/v2/leases', { params }),
  get: (id: string) => http.get<Lease>(`/leases/${id}`),
  /** POST /leases → `payment-pending` when a fee is due, else `active`. 403 for validation, 404 for a foreign property. */
  create: (body: LeaseCreateInput) => http.post<LeaseCreateResponse>('/leases', body),
  /** PATCH /leases/:id/cancel — landlord: canceled + property vacant; tenant: cancellation-requested. */
  cancel: (leaseId: string, reason?: string) => http.patch<null>(`/leases/${leaseId}/cancel`, reason ? { reason } : {}),
  /** POST /leases/:id/remind — 403 not active / nothing due, 409 already reminded today. */
  remind: (leaseId: string) => http.post<{ sent: boolean }>(`/leases/${leaseId}/remind`, {}),
  /** GET /leases/:id/rents — newest first; both roles. */
  rents: (leaseId: string, page = 1, size = 50) => http.get<RentListResponse>(`/leases/${leaseId}/rents`, { params: { page, size } }),
  /** GET /leases/:id/rents/:rentId/receipt → presigned URL, 1 h. */
  receipt: (leaseId: string, rentId: string) => http.get<{ url: string }>(`/leases/${leaseId}/rents/${rentId}/receipt`),
}
